import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ViewUsersTable from "./components/ViewUsersTable/ViewUsersTable";
import MainPageLayout from "../../components/header/MainPageLayout";
import { PageTitle, PageTitleSection, TableCard } from "./ViewUsersPage.styles";
// Language Context
import { useLanguage } from "../../core/translations/LanguageContext";
import { getJwt } from "../../core/LocalStorage/AuthSession/core";
import {
    GetAllUsersType,
    UserRow,
} from "../../core/ServerApiLayer/VOne/institutions/get_users";
import { assignRole } from "../../core/ServerApiLayer/VOne/users/assign_role";
import { unassignRole } from "../../core/ServerApiLayer/VOne/users/unassign_role";
import { assignLicense } from "../../core/ServerApiLayer/VOne/licenses/assign";
import { unassignLicense } from "../../core/ServerApiLayer/VOne/licenses/unassign";
import { GetLicensesType } from "../../core/ServerApiLayer/VOne/institutions/get_available_licenses";

// Translations
import { UsersPageTranslations } from "./translations/types";
import { enTranslations } from "./translations/en";
import { deTranslations } from "./translations/de";
import { SupportedLanguage } from "../../core/translations/supportedLanguages";
import BackButton from "../../components/button/BackButton/BackButton";
import {
    AssignLicenseType,
    UnassignLicenseType,
} from "../../core/ServerApiLayer/VOne/licenses/assign";
import {
    passwordResetRequest,
    PasswordResetRequestType,
} from "../../core/ServerApiLayer/VOne/users/request_password";

const TRANSLATIONS: Record<SupportedLanguage, UsersPageTranslations> = {
    en: enTranslations,
    de: deTranslations,
};

export interface ViewUsersPageProps {
    getAllUsers: GetAllUsersType;
    unassignLicense: UnassignLicenseType;
    assignLicense: AssignLicenseType;
    getLicenses: GetLicensesType;
    requestPassword: PasswordResetRequestType;
}

/**
 * ViewUsersPage Component
 *
 * This component fetches and displays users for either an institution or a specific department.
 * Admins can manage roles and licenses.
 *
 * @returns A ViewUsersPage component
 */
function ViewUsersPage({
    getAllUsers,
    assignLicense,
    unassignLicense,
    getLicenses,
    requestPassword,
}: ViewUsersPageProps) {
    const { language } = useLanguage();
    const navigate = useNavigate(); // Move useNavigate inside the component
    const { institutionId, departmentId } = useParams<{
        institutionId: string;
        departmentId?: string;
    }>();

    const [users, setUsers] = useState<UserRow[]>([]);
    const [loading, setLoading] = useState(true);

    // Re-read the users from the server. Every mutation on this page calls this
    // afterwards rather than hand-patching `users`, so what the table shows is
    // always what the backend actually did — a local patch that guessed wrong
    // (or was skipped on an unexpected status code) is what made added and
    // removed licenses appear not to take effect at all.
    const fetchUsers = useCallback(async () => {
        const jwt = getJwt();

        // A request must be scoped to either an institution or a department.
        if (!institutionId && !departmentId) {
            console.error("An institution ID or a department ID is required.");
            setLoading(false);
            return;
        }

        // Call API scoped by institution and/or department. A department is
        // keyed by its own id, so the institution id may be absent.
        const response = await getAllUsers(
            //@ts-ignore
            jwt,
            institutionId ? Number(institutionId) : undefined,
            departmentId ? Number(departmentId) : undefined,
        );

        if (response.status === 200 && response.data) {
            // The endpoint returns one entry per role, so collapse them into one
            // row per user with their roles as a comma-separated string.
            const formattedUsers = response.data.reduce<UserRow[]>(
                (acc, user) => {
                    const existingUser = acc.find((u) => u.id === user.id);

                    if (existingUser) {
                        // Combine roles without duplicating the user
                        existingUser.role = Array.from(
                            new Set([
                                ...existingUser.role.split(", "),
                                ...user.role.map((r) => r.role_name),
                            ]),
                        ).join(", ");
                    } else {
                        acc.push({
                            ...user,
                            role: user.role.map((r) => r.role_name).join(", "),
                        });
                    }

                    return acc;
                },
                [],
            );

            setUsers(formattedUsers);
        }
        setLoading(false);
    }, [getAllUsers, institutionId, departmentId]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // A department-scoped view assigns *department* roles; an institution-scoped
    // view assigns *institution* roles. The backend rejects an institution-scope
    // request with no institution_id (which is exactly what a department view
    // has), so the scope and entity must follow the view we are in.
    const roleScope = departmentId ? "department" : "institution";
    const roleInstitutionId =
        !departmentId && institutionId ? Number(institutionId) : undefined;
    const roleDepartmentId = departmentId ? Number(departmentId) : undefined;

    const handleAssignRole = async (userId: number, roleName: string) => {
        const jwt = getJwt();
        if (!jwt) return;

        await assignRole(
            jwt,
            userId,
            roleName,
            roleScope,
            roleInstitutionId,
            roleDepartmentId,
        );

        await fetchUsers();
    };

    const handleUnassignRole = async (userId: number, roleName: string) => {
        const jwt = getJwt();
        if (!jwt) return;

        await unassignRole(
            jwt,
            userId,
            roleName,
            roleScope,
            roleInstitutionId,
            roleDepartmentId,
        );

        await fetchUsers();
    };

    // The license handlers hand their outcome back so the modal can report a
    // failure and stay open, rather than closing over an unchanged table.
    const handleAssignLicense = async (userId: number, licenseKey: string) => {
        const jwt = getJwt();
        if (!jwt) return { ok: false, error: "Your session has expired." };

        const response = await assignLicense(jwt, userId, licenseKey);
        await fetchUsers();

        return response.error
            ? { ok: false, error: response.error }
            : { ok: true };
    };

    const handleUnassignLicense = async (
        userId: number,
        licenseKey: string,
    ) => {
        const jwt = getJwt();
        if (!jwt) return { ok: false, error: "Your session has expired." };

        const response = await unassignLicense(jwt, userId, licenseKey);
        await fetchUsers();

        return response.error
            ? { ok: false, error: response.error }
            : { ok: true };
    };

    //@ts-ignore
    const translations = TRANSLATIONS[language];

    // Logical parent, one level up the nested URL:
    //   /institution/:id/department/:deptId -> /institution/:id/departments
    //   /institution/:id/users              -> /institution/:id
    //   /department/:deptId/users (fallback for a department admin with no
    //     institution context) -> the landing page.
    let backTo = "/institutions";
    if (institutionId && departmentId) {
        backTo = `/institution/${institutionId}/departments`;
    } else if (institutionId) {
        backTo = `/institution/${institutionId}`;
    } else if (departmentId) {
        backTo = "/home";
    }

    return (
        <MainPageLayout>
            <BackButton navigate={navigate} to={backTo} />
            <TableCard>
                <PageTitleSection>
                    <PageTitle>{translations.title}</PageTitle>
                </PageTitleSection>
                <ViewUsersTable
                    data={users}
                    loadingUsers={loading}
                    assignLicenseRequest={handleAssignLicense}
                    removeLicenseRequest={handleUnassignLicense}
                    getLicenses={getLicenses}
                    requestPassword={requestPassword}
                    handleAssignRole={handleAssignRole}
                    handleUnassignRole={handleUnassignRole}
                />
            </TableCard>
        </MainPageLayout>
    );
}

export default ViewUsersPage;
